import { Injectable } from '@angular/core';
import Budget from '../models/Budget';

/**
 * Service to replicate the monthly & hourly budget cost logic used in budgets-list component
 * so financial components can compute consistent employee cost figures.
 */
@Injectable({ providedIn: 'root' })
export class BudgetCalculationService {
    private readonly WORKING_DAYS_PER_MONTH = 17;
    private readonly HOURS_PER_DAY = 8;
    private readonly STANDARD_WORKING_HOURS = this.WORKING_DAYS_PER_MONTH * this.HOURS_PER_DAY; // 136

    /** Range check aligned with budgets-list (isAfter/isBefore => strict, not inclusive). */
    private isWithinRange(selectedTs: number, startStr: string, endStr: string): boolean {
        const startTs = new Date(startStr).getTime();
        const endTs = new Date(endStr).getTime();
        return selectedTs > startTs && selectedTs < endTs;
    }

    /** Simple prorate identical to budgets-list.simpleProrate */
    private simpleProrate(totalSpend: number, startDateStr: string, endDateStr: string): number {
        const start = new Date(startDateStr);
        const end = new Date(endDateStr);
        // budgets-list subtracts a day before deriving months; mimic logic
        end.setDate(end.getDate() - 1);
        if (end < start) return 0;
        const yearDiff = end.getFullYear() - start.getFullYear();
        const monthDiff = end.getMonth() - start.getMonth();
        const baseMonths = yearDiff * 12 + monthDiff;
        const extra = end.getDate() >= start.getDate() ? 1 : 0;
        const months = baseMonths + extra;
        if (months <= 0) return 0;
        return Math.round((totalSpend / months + Number.EPSILON) * 100) / 100;
    }

    /** Filter budgets active on targetDate and not archived. */
    private getActiveBudgets(budgets: Budget[], targetDate: Date): Budget[] {
        const ts = targetDate.getTime();
        return (budgets || []).filter((b) => !b.archived && this.isWithinRange(ts, b.startDate, b.endDate));
    }

    private getBudgetsByType(budgetType: string | null, budgets: Budget[]): Budget[] {
        if (budgetType) {
            return budgets.filter((b: any) => b.repeat === budgetType && b.recurring === true);
        }
        return budgets.filter((b: any) => b.repeat == null || b.recurring === false);
    }

    /**
     * Computes the monthly total budget for an employee for a given date.
     * Mirrors getBudgetedCostUsingList in budgets-list.component.ts
     */
    computeMonthlyBudgetTotal(budgets: Budget[], targetDate: Date): number {
        const active = this.getActiveBudgets(budgets, targetDate);
        if (!active.length) return 0;
        const monthly = this.getBudgetsByType('MONTHLY', active);
        const yearly = this.getBudgetsByType('YEARLY', active);
        const daily = this.getBudgetsByType('DAILY', active);
        const nonRecurring = this.getBudgetsByType(null, active);

        const nonRecurringMonthly = nonRecurring.map((b) => ({ ...b, totalSpend: this.simpleProrate(b.totalSpend, b.startDate, b.endDate) }));
        const yearlyMonthly = yearly.map((b) => ({ ...b, totalSpend: b.totalSpend / 12 }));
        const dailyMonthly = daily.map((b) => ({ ...b, totalSpend: b.totalSpend * this.WORKING_DAYS_PER_MONTH }));

        const all = [
            ...nonRecurringMonthly,
            ...monthly,
            ...yearlyMonthly,
            ...dailyMonthly,
        ];
        return all.reduce((acc, b) => acc + (b.totalSpend || 0), 0);
    }

    /** 
     * Hourly budgeted cost = monthly total / standard hours, adjusted for work percentage
     * @param monthlyTotal The monthly budget total
     * @param workPercentage The employee's work percentage (100 = full-time, 80 = 80%, etc.)
     */
    computeHourlyBudgetedCost(monthlyTotal: number, workPercentage?: number): number {
        if (!monthlyTotal) return 0;
        const percentage = workPercentage != null && workPercentage > 0 ? workPercentage : 100;
        const adjustedHours = this.STANDARD_WORKING_HOURS * (percentage / 100);
        return monthlyTotal / adjustedHours;
    }

    /** 
     * Project cost for employee = hourly cost * hours worked on project
     * @param monthlyTotal The monthly budget total
     * @param hoursWorked Hours worked on the project
     * @param workPercentage The employee's work percentage (100 = full-time, 80 = 80%, etc.)
     */
    computeProjectCost(monthlyTotal: number, hoursWorked: number, workPercentage?: number): { hourlyCost: number; projectCost: number } {
        const hourlyCost = this.computeHourlyBudgetedCost(monthlyTotal, workPercentage);
        const projectCost = hourlyCost * (hoursWorked || 0);
        return { hourlyCost, projectCost };
    }
}
